import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import useAuthStore from '../hooks/useAuthStore';
import { getClassLevelFromStream, getClassData, academicResourceManifest } from '../data/academicDatabase.js';

export default function PastPapers() {
  const navigate = useNavigate();
  const { theme, user } = useAuthStore();
  const isICSE = theme === 'icse';
  const primaryColor = isICSE ? 'icse-teal' : 'isc-coral';
  const secondaryColor = isICSE ? 'icse-cyan' : 'isc-purple';

  const [selectedPaper, setSelectedPaper] = useState(null);
  const [classData, setClassData] = useState(null);
  const [showClassPrompt, setShowClassPrompt] = useState(false);
  const [manifestResources, setManifestResources] = useState([]);
  const [resourceStats, setResourceStats] = useState({ total: 0, available: 0, pending: 0 });

  // Load AdSense script when component mounts
  useEffect(() => {
    if (!globalThis.adsbygoogle) {
      const script = document.createElement('script');
      script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js';
      script.async = true;
      script.crossOrigin = 'anonymous';
      document.head.appendChild(script);
    }
  }, []);

  // Get dynamic class data based on user's stream
  useEffect(() => {
    if (user?.stream) {
      const classLevel = getClassLevelFromStream(user.stream);
      const data = getClassData(classLevel);
      setClassData(data);
      setShowClassPrompt(false);
      
      // Get manifest resources for this class
      let className;
      if (classLevel === 9) className = 'ICSE-Class-9';
      else if (classLevel === 10) className = 'ICSE-Class-10';
      else if (classLevel === 11) className = 'ISC-Class-11';
      else className = 'ISC-Class-12';
      
      const resources = academicResourceManifest.getAvailableResources(className);
      const pending = academicResourceManifest.getPendingDownloads(className);
      const stats = academicResourceManifest.getTotalStats();
      
      setManifestResources([...resources, ...pending]);
      setResourceStats(stats);
      
      // Reset selection when class changes
      setSelectedPaper(null);
    } else {
      setShowClassPrompt(true);
    }
  }, [user?.stream]);

  const pastPapers = classData?.pastPapers || [];

  const handlePaperSelect = (paper) => {
    setSelectedPaper(paper);
  };

  const handleDownload = (paper) => {
    // Check if resource is available in manifest
    const classLevel = getClassLevelFromStream(user.stream);
    let className;
    if (classLevel === 9) className = 'ICSE-Class-9';
    else if (classLevel === 10) className = 'ICSE-Class-10';
    else if (classLevel === 11) className = 'ISC-Class-11';
    else className = 'ISC-Class-12';
    
    const availableResources = academicResourceManifest.getAvailableResources(className, paper.subject);
    const isAvailable = availableResources.some(r => r.year === paper.year);
    
    if (isAvailable) {
      // In a real application, this would download the actual PDF file
      // For now, show information about where to find the resource
      alert(`This paper is marked as available in the manifest.\n\nTo download the actual PDF:\n1. Visit the resource sources listed in Downloaded_Resources_Log.md\n2. Download from sites like Shaalaa.com, CISCE official site, etc.\n3. Save with naming convention: ${paper.year}_${paper.subject}_PastPaper.pdf\n4. Run: node scripts/syncDownloads.js to register the file`);
    } else {
      alert(`This paper is currently pending download.\n\nTo download:\n1. Visit Shaalaa.com or other educational sites\n2. Search for ${paper.subject} ${paper.year} past paper\n3. Download and save to: downloaded-resources/${className}/${paper.subject}/\n4. Use filename: ${paper.year}_${paper.subject}_PastPaper.pdf\n5. Run: node scripts/syncDownloads.js to register`);
    }
  };

  // Render class selection prompt for users without class
  const renderClassPrompt = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-2xl mx-auto text-center"
    >
      <div className="glass-card rounded-3xl p-12 border border-white/10">
        <div className="text-6xl mb-6">📄</div>
        <h2 className="text-3xl font-bold text-white mb-4">Select Your Class</h2>
        <p className="text-gray-400 mb-8">
          Please select your class in your profile to view relevant past papers and solutions.
        </p>
        <button
          onClick={() => navigate('/profile')}
          className="px-8 py-4 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white font-medium rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/25"
        >
          Go to Profile Settings
        </button>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen w-full text-white antialiased bg-[linear-gradient(180deg,_#0d0b18_0%,_#16122c_50%,_#0b0914_100%)] relative overflow-x-hidden">
      {/* Soft, silk-like velvet glow overlay */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-purple-600/5 rounded-full blur-[120px] pointer-events-none" />

      <nav className="fixed top-0 left-0 right-0 z-50 glass-panel border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => selectedPaper ? setSelectedPaper(null) : navigate('/')}
              className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-xl font-bold text-white">Past Papers</h1>
              <p className={`text-${primaryColor}-400 text-sm`}>
                {selectedPaper ? `${selectedPaper.subject} ${selectedPaper.year}` : classData?.className || 'Select a paper to view'}
              </p>
            </div>
          </div>

          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
          >
            Back to Portal
          </button>
        </div>
      </nav>

      <main className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          {showClassPrompt && renderClassPrompt()}
          {!showClassPrompt && !selectedPaper && (
            <div className="space-y-8">
              {/* Resource Statistics */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card rounded-2xl p-6 border border-white/10"
              >
                <h3 className="text-xl font-bold text-white mb-4">Resource Statistics</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className={`text-3xl font-bold text-${primaryColor}-400 mb-1`}>{resourceStats.total}</div>
                    <div className="text-gray-400 text-sm">Total Resources</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-400 mb-1">{resourceStats.available}</div>
                    <div className="text-gray-400 text-sm">Available</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-yellow-400 mb-1">{resourceStats.pending}</div>
                    <div className="text-gray-400 text-sm">Pending Download</div>
                  </div>
                </div>
              </motion.div>

              {/* Available Past Papers */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              >
                {pastPapers.map((paper, index) => (
                  <motion.div
                    key={paper.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    onClick={() => handlePaperSelect(paper)}
                    className="glass-card rounded-2xl p-6 cursor-pointer hover:scale-[1.02] transition-all border border-white/10"
                  >
                    <div className="text-4xl mb-4">📄</div>
                    <h3 className="text-xl font-bold text-white mb-2">{paper.subject}</h3>
                    <p className="text-gray-400 text-sm mb-4">{paper.year} Past Paper</p>
                    <div className="flex items-center justify-between">
                      <span className={`text-${primaryColor}-400 font-medium`}>{paper.year}</span>
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">Available</span>
                        <div className={`w-8 h-8 rounded-lg bg-gradient-to-br from-${primaryColor}-500 to-${secondaryColor}-500 flex items-center justify-center text-white`}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>

              {/* Pending Resources from Manifest */}
              {manifestResources.some(r => r.status === 'pending_download') && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <h3 className="text-xl font-bold text-white mb-4">Coming Soon</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {manifestResources.filter(r => r.status === 'pending_download').slice(0, 6).map((resource, index) => (
                      <motion.div
                        key={`${resource.filename}-${index}`}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.05 }}
                        className="glass-card rounded-xl p-4 border border-white/5 opacity-60"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="text-white font-medium text-sm">{resource.filename.replaceAll('_', ' ').replaceAll('.pdf', '')}</h4>
                            <p className="text-gray-400 text-xs">{resource.year}</p>
                          </div>
                          <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">Pending</span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  {manifestResources.filter(r => r.status === 'pending_download').length > 6 && (
                    <p className="text-gray-400 text-sm mt-4 text-center">
                      +{manifestResources.filter(r => r.status === 'pending_download').length - 6} more resources coming soon
                    </p>
                  )}
                </motion.div>
              )}
            </div>
          )}
          {!showClassPrompt && selectedPaper && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-6"
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="glass-card rounded-2xl p-8 border border-white/10"
              >
                <h2 className="text-2xl font-bold text-white mb-6">Question Paper</h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                    <div>
                      <h3 className="text-white font-medium">{selectedPaper.subject}</h3>
                      <p className="text-gray-400 text-sm">{selectedPaper.year} Past Paper</p>
                    </div>
                    <span className={`text-${primaryColor}-400 font-bold`}>{selectedPaper.year}</span>
                  </div>

                  {/* Google AdSense Container */}
                  <div className="mt-6 p-4 bg-white/5 rounded-xl border border-white/10">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs text-gray-500 uppercase tracking-wider">Advertisement</span>
                      <span className="text-xs text-gray-600">Ad</span>
                    </div>
                    <div className="w-full min-h-[250px] bg-white/10 rounded-lg flex items-center justify-center">
                      <ins
                        className="adsbygoogle"
                        style={{ display: 'block' }}
                        data-ad-client="ca-pub-XXXXXXXXXXXXXXXX"
                        data-ad-slot="XXXXXXXXXX"
                        data-ad-format="auto"
                        data-full-width-responsive="true"
                      />
                    </div>
                  </div>

                  <button
                    onClick={() => handleDownload(selectedPaper)}
                    className={`w-full py-4 bg-gradient-to-r from-${primaryColor}-500 to-${secondaryColor}-500 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download PDF
                  </button>

                  <div className="mt-6 p-4 bg-white/5 rounded-xl">
                    <h4 className="text-white font-medium mb-2">Paper Details</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Duration:</span>
                        <span className="text-white">3 hours</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Total Marks:</span>
                        <span className="text-white">80</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Sections:</span>
                        <span className="text-white">A, B, C</span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="glass-card rounded-2xl p-8 border border-white/10"
              >
                <h2 className="text-2xl font-bold text-white mb-6">Solutions</h2>
                <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                  {selectedPaper.markingSchemeSolutions?.map((question, index) => (
                    <motion.div
                      key={question.qNo}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="p-4 bg-white/5 rounded-xl border border-white/5"
                    >
                      <div className="flex items-start gap-3 mb-3">
                        <div className={`w-8 h-8 rounded-lg bg-gradient-to-br from-${primaryColor}-500 to-${secondaryColor}-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}>
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <p className="text-white text-sm font-medium mb-1">{question.questionText}</p>
                          <p className="text-gray-400 text-xs">{question.section}</p>
                        </div>
                      </div>
                      <div className="ml-11 p-3 bg-white/5 rounded-lg">
                        <div className="text-gray-300 text-sm" dangerouslySetInnerHTML={{ __html: question.solution }} />
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
}
